<template>
  <div v-rect-loading="loading" class="notification-editor-page">
    <div class="header">
      <div class="title">{{ $t(titleI18nKey) }}</div>
    </div>
    <div class="content">
      <WrappedFrom
        ref="formEditor"
        class="notification-management-info"
        :model="form"
        :formLines="formLines"
      >
        <template #tinyMceEditor="{ line, lineName }">
          <TinymceEditor
            v-if="showTinyMceEditor"
            v-model="form[lineName]"
            isLayout
            class="edit-cn"
            :isCnContent="line.isCnContent"
            :templateFields="templateFields"
            :showTemplateField="true"
            :disabled="isViewMode"
          />
          <div class="send-email-test-button">
            <el-button type="primary" :disabled="isViewMode" @click="sendEmailTest(!line.isCnContent)">
              {{ $t(`${i18nPrefix}.btn.sendEmailTestButton`) }}
            </el-button>
          </div>
        </template>
      </WrappedFrom>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="toTemplateList">{{ $t('common.btn.cancel') }}</el-button>
        <el-button class="save-btn" :disabled="isViewMode" @click="saveOrUpdateTemplate">{{ $t('common.btn.save') }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { addI18nProperty } from '@/i18n'
import { mapActions } from 'vuex'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import TinymceEditor from '../tinymceEditor.vue'

export default {
  components: {
    WrappedFrom,
    TinymceEditor
  },
  data () {
    return {
      loading: false,
      showTinyMceEditor: false,
      i18nPrefix: 'admin.company.notification',
      layoutTemplateOptions: [],
      form: {
        content_cn: '',
        content_en: '',
        template_type_name: null,
        name: null,
        layout_id: null,
        subject_cn: null,
        subject_en: null,
        company: null,
        email_sender_address: null,
        sender_name_cn: null,
        sender_name_en: null,
        has_i18n: true
      },
      templateFields: []
    }
  },
  computed: {
    titleI18nKey () {
      return this.$route.meta.titleI18nKey
    },
    templateId () {
      return this.$route.params.templateId
    },
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    isViewMode () {
      return this.$route.meta.isViewMode
    },
    refForm () {
      return this.$refs.formEditor
    },
    formLines () {
      const hasI18n = this.form.has_i18n
      const requiredMessage = this.$t('common.message.requiredFormField')
      return {
        layout_id: {
          elType: 'el-select',
          option: {
            data: this.layoutTemplateOptions
          },
          events: {
            change: this.handleLayoutTemplateChange
          },
          disabled: !hasI18n,
          label: this.$t(`${this.i18nPrefix}.label.layoutTemplateLoad`)
        },
        templateTypeName: {
          label: this.$t(`${this.i18nPrefix}.label.templateType`),
          disabled: !!this.templateId || this.isViewMode,
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        name: {
          label: this.$t(`${this.i18nPrefix}.label.templateName`),
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        subject_cn: {
          label: this.$t(`${this.i18nPrefix}.label.${hasI18n ? 'subjectCn' : 'subject'}`),
          rules: [{ required: true, message: requiredMessage, trigger: 'blur' }]
        },
        subject_en: {
          label: this.$t(`${this.i18nPrefix}.label.subjectEn`),
          hidden: !hasI18n
        },
        receiver_limit: {
          label: this.$t(`${this.i18nPrefix}.label.receiverLimit`)
        },
        content_cn: {
          label: this.$t(`${this.i18nPrefix}.label.${hasI18n ? 'emailEditCn' : 'emailEdit'}`),
          contentSlot: 'tinyMceEditor',
          isCnContent: true
        },
        content_en: {
          label: this.$t(`${this.i18nPrefix}.label.emailEditEn`),
          contentSlot: 'tinyMceEditor',
          hidden: !hasI18n
        }
      }
    }
  },
  created () {
    this.initData()
  },
  methods: {
    ...mapActions('notificationTemplate', [
      'updateSystemTemplate',
      'getSystemTemplateById',
      'createTemplate',
      'searchSystemLayoutTemplate',
      'getSystemLayoutTemplateById',
      'searchTemplateField',
      'sendEmail'
    ]),
    async initData () {
      this.loading = true
      try {
        await Promise.all([
          this.loadTemplate(),
          this.getLayoutTemplateList(),
          this.loadLayoutTemplate()
        ])
      } finally {
        this.loading = false
      }
    },
    async saveOrUpdateTemplate () {
      let result = false
      result = await this.refForm.submit()
      if (!result) {
        return
      }
      if (this.form.layout_id === -1) {
        this.form.layout_id = null
      }
      const params = {
        ...this.form,
        id: this.templateId,
        system_template: true
      }
      this.$eventBus.$emit('showLoading', true)
      let resp = null
      try {
        if (this.isEditMode) {
          resp = await this.updateSystemTemplate(params)
        } else {
          resp = await this.createTemplate(params)
        }
        if (resp) {
          this.toTemplateList()
        }
      } catch (error) {
        if (!this.form.layout_id) {
          this.form.layout_id = -1
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async sendEmailTest (isEn) {
      if (this.form.layout_id === -1) {
        this.form.layout_id = null
      }
      const params = {
        ...this.form,
        is_en: isEn
      }
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.sendEmail(params)
        if (resp) {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.sendSuccess`))
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async loadTemplate () {
      if (this.templateId) {
        const resp = await this.getSystemTemplateById(this.templateId)
        if (resp) {
          addI18nProperty(resp, 'templateTypeName', 'template_type_name_cn', 'template_type_name_en')
          if (!resp.layout_id) {
            resp.layout_id = -1
          }
          this.form = resp
          await this.loadTemplateField()
        }
      }
      this.showTinyMceEditor = true
    },
    async loadTemplateField () {
      const resp = await this.searchTemplateField(this.form.template_type_id)
      if (resp) {
        this.templateFields = _.map(resp, (templateField) => {
          addI18nProperty(templateField, 'i18nTitle', 'title_cn', 'title_en')
          templateField.description = templateField.description || ''
          return templateField
        })
      }
    },
    async getLayoutTemplateList () {
      const resp = await this.searchSystemLayoutTemplate()
      if (resp) {
        const layoutTemplateOptions = _.map(resp, (layoutTemplate) => ({
          value: layoutTemplate.id,
          label: layoutTemplate.name
        }))

        this.layoutTemplateOptions = [
          {
            value: -1,
            label: this.$t(`${this.i18nPrefix}.label.searchUpLayout`)
          },
          ...layoutTemplateOptions
        ]
      }
    },
    toTemplateList () {
      this.$router.push({
        path: '/v-admin/system-notification-template-management',
        query: this.$route.query
      })
    },
    async handleLayoutTemplateChange () {
      this.$eventBus.$emit('showLoading', true)
      try {
        await this.loadLayoutTemplate()
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async loadLayoutTemplate () {
      if (!this.form.layout_id) return
      const layoutTemplateId = this.form.layout_id
      const resp = await this.getSystemLayoutTemplateById(layoutTemplateId)
      this.form.sender_name_en = resp.sender_name_en
      this.form.sender_name_cn = resp.sender_name_cn
      this.form.email_sender_address = resp.email_sender_address
      const emailHeader = '</email-header>'
      const emailFooter = '<email-footer>'
      const emailHeaderLength = emailHeader.length
      if (resp && this.form.has_i18n) {
        let contentCn = this.form.content_cn
        if (contentCn.lastIndexOf(emailHeader) !== -1) {
          contentCn = contentCn.slice(contentCn.lastIndexOf(emailHeader) + emailHeaderLength, contentCn.length)
        }
        if (contentCn.lastIndexOf(emailFooter) !== -1) {
          contentCn = contentCn.slice(0, contentCn.lastIndexOf(emailFooter))
        }
        if (!contentCn) {
          contentCn = '<div></div>'
        }
        this.form.content_cn = [resp.content_header_cn, contentCn, resp.content_footer_cn].join('')

        let contentEn = this.form.content_en
        if (contentEn.lastIndexOf(emailHeader) !== -1) {
          contentEn = contentEn.slice(contentEn.lastIndexOf(emailHeader) + emailHeaderLength, contentEn.length)
        }
        if (contentEn.lastIndexOf(emailFooter) !== -1) {
          contentEn = contentEn.slice(0, contentEn.lastIndexOf(emailFooter))
        }
        if (!contentEn) {
          contentEn = '<div></div>'
        }
        this.form.content_en = [resp.content_header_en, contentEn, resp.content_footer_en].join('')
      }
    }
  },
  watch: {
    '$i18n.locale' () {
      this.showTinyMceEditor = false
      this.$nextTick(() => {
        this.showTinyMceEditor = true
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.notification-editor-page {
  .header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;

    .title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .content {
    width: 980px;
    margin: 30px auto 0;
  }

  .bottom-btn-group {
    display: flex;
    justify-content: flex-end;

    .el-button {
      padding: 8px 28px;
      border-radius: 2px;
      line-height: 22px;
      font-size: 16px;
      font-weight: 500;
      color: #0096cc;

      &:last-child {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }

  .send-email-test-button {
    margin-top: 20px;
    text-align: right;
  }
}
</style>
